import type { BrandedPdfInput } from "@/lib/pdfBranded";

export type LocalDoc = {
  id: string;
  tool: string;
  title: string;
  file_name: string;
  payload: Omit<BrandedPdfInput, "branding">;
  created_at: string;
};

export type LocalChatMessage = {
  conversation_id: string;
  role: "user" | "assistant";
  content: string;
  created_at: string;
};

const DOCS_KEY = "mt:docs";
const CHATS_KEY = "mt:chats";

function readArray<T>(key: string): T[] {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(localStorage.getItem(key) || "[]") as T[];
  } catch {
    return [];
  }
}

function writeArray<T>(key: string, value: T[]) {
  if (typeof window === "undefined") return;
  localStorage.setItem(key, JSON.stringify(value));
  window.dispatchEvent(new Event(`${key}-changed`));
}

/* ---------- PDF docs ---------- */

export function listDocs(): LocalDoc[] {
  return readArray<LocalDoc>(DOCS_KEY).sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export function saveDoc(doc: Omit<LocalDoc, "id" | "created_at">) {
  const all = readArray<LocalDoc>(DOCS_KEY);
  all.push({
    ...doc,
    id: crypto.randomUUID(),
    created_at: new Date().toISOString(),
  });
  // limita a 200 últimos
  while (all.length > 200) all.shift();
  writeArray(DOCS_KEY, all);
}

export function deleteDoc(id: string) {
  writeArray(
    DOCS_KEY,
    readArray<LocalDoc>(DOCS_KEY).filter((d) => d.id !== id),
  );
}

export function subscribeDocs(handler: () => void) {
  const event = `${DOCS_KEY}-changed`;
  window.addEventListener(event, handler);
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(event, handler);
    window.removeEventListener("storage", handler);
  };
}

/* ---------- Chat messages ---------- */

export function listChatMessages(): LocalChatMessage[] {
  return readArray<LocalChatMessage>(CHATS_KEY);
}

export function saveChatMessage(msg: LocalChatMessage) {
  const all = readArray<LocalChatMessage>(CHATS_KEY);
  all.push(msg);
  // limita a 2000 mensagens recentes
  while (all.length > 2000) all.shift();
  writeArray(CHATS_KEY, all);
}

export function deleteConversation(conversationId: string) {
  writeArray(
    CHATS_KEY,
    readArray<LocalChatMessage>(CHATS_KEY).filter((m) => m.conversation_id !== conversationId),
  );
}

export function subscribeChats(handler: () => void) {
  const event = `${CHATS_KEY}-changed`;
  window.addEventListener(event, handler);
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(event, handler);
    window.removeEventListener("storage", handler);
  };
}
